import { DateTime } from "luxon";

import { ShiftLockState } from "../utilities/helpers/shiftLockStateHelpers";
import { ajaxController } from "./controller/AjaxController";
import { ShiftExistenceResponseDTO } from "../DTOs/Settings/ShiftExistenceDTO";
import { RoleConfig, SectionNameOverrides } from "../DTOs/Settings/RoleConfigDto";
import { ShiftLetter } from "../types/Shift";
import { CurrencyDTO } from "../utilities/format/currency";

class SettingsAjax {
  private _ajaxController = ajaxController
  private _settingsDataEndpoint = "/Settings"

  public getTimezoneSettings() {
    return this._ajaxController.get<{ timezoneId: string }>(
      `${this._settingsDataEndpoint}/timezone`,
      {}
    )
  }

  public getCurrency() {
    return this._ajaxController.get<CurrencyDTO>(`${this._settingsDataEndpoint}/currency`, {});
  }

  public getShiftBoundaries(shiftDate: DateTime, dayOrNight: ShiftLetter) {
    return this._ajaxController.get<{ shiftStart: DateTime, shiftEnd: DateTime }>(
      `${this._settingsDataEndpoint}/shift-boundaries/${shiftDate.toISODate()}/${dayOrNight}`,
      {}
    )
  }

  public getShiftEditability(shiftDate: DateTime, dayOrNight: ShiftLetter) {
    return this._ajaxController.get<{ isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null; exists?: boolean }>(
      `${this._settingsDataEndpoint}/shift-editability/${shiftDate.toISODate()}/${dayOrNight}`,
      {}
    )
  }

  public getShiftLockInfo() {
    return this._ajaxController.get<ShiftLockState>(
      `${this._settingsDataEndpoint}/shift-lock-info`,
      {},
    )
  }

  public unlockShift(shiftDate: DateTime, dayOrNight: ShiftLetter) {
    return this._ajaxController.post<{}, { isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null }>(
      `${this._settingsDataEndpoint}/shifts/${shiftDate.toISODate()}/${dayOrNight}/unlock`,
      {}
    )
  }

  public relockShift(shiftDate: DateTime, dayOrNight: ShiftLetter) {
    return this._ajaxController.post<{}, { isLocked: boolean; canToggleLock: boolean; isPastAutoLockCutOff: boolean | null }>(
      `${this._settingsDataEndpoint}/shifts/${shiftDate.toISODate()}/${dayOrNight}/relock`,
      {}
    )
  }

  public getShiftExistence(dateFromIso: string, dateToIso: string) {
    return this._ajaxController.get<ShiftExistenceResponseDTO>(
      `${this._settingsDataEndpoint}/shifts/existence`,
      { dateFrom: dateFromIso, dateTo: dateToIso }
    );
  }

  public getRoleConfig() {
    return this._ajaxController.get<RoleConfig>(
      `${this._settingsDataEndpoint}/role-config`,
      {}
    );
  }

  public getSectionNames() {
    return this._ajaxController.get<SectionNameOverrides>(
      `${this._settingsDataEndpoint}/section-names`,
      {}
    );
  }

  public setSectionName(sectionKey: string, names: Record<string, string | null>) {
    return this._ajaxController.put<{ names: Record<string, string | null> }, boolean>(
      `${this._settingsDataEndpoint}/section-names/${sectionKey}`,
      { names }
    );
  }

  public suggestSectionNames(name: string, builtIn: Record<string, string>) {
    return this._ajaxController.post<{ name: string; builtIn: Record<string, string> }, Record<string, string>>(
      `${this._settingsDataEndpoint}/section-names/suggest`,
      { name, builtIn }
    );
  }
}

export const settingsAjax = new SettingsAjax();